import RestrictedComponent from "common/components/RestrictedComponent";
import { SubscriptionDetailsCard } from "common/components/subscriptions";
import { Tag } from "common/components/ui/Tag";
import useCustomer from "common/datahooks/useCustomer";
import useEstimateRenewal from "common/datahooks/useEstimateRenewal";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import { formatPrice } from "common/helpers/utils";

import SubscriptionCardSeatsSummary from "../../../SubscriptionCardSeatsSummary";
import NonRenewingTag from "../NonRenewingTag";
import Price from "../Price";
import RemoveChangeButton from "../RemoveChangeButton";
import SubscriptionInfo from "../SubscriptionInfo";
import {
  ActivateSubscriptionButton,
  ManageSubscriptionButton,
  ReactivateSubscriptionButton,
  StartTrialButton,
} from "./ActionButtons";
import ScheduledChangeTag from "./ScheduledChangeTag";

export default function LinkedInCrm() {
  const {
    id: workspaceId,
    subscription,
    licenses,
    lifetime_license_count: lifetimeLicenseCount,
    license_count: licenseCount,
  } = useSelectedWorkspaceContext();
  const { workspaceAccounts } = useWorkspaceAccounts(workspaceId);
  const { customerInfo } = useCustomer();

  const { estimateRenewal, isLoadingEstimateRenewal } =
    useEstimateRenewal(subscription);

  let tagComponent = <Tag variant="disabled">Inactive</Tag>;
  let buttonComponent = customerInfo.had_trial ? (
    <ActivateSubscriptionButton />
  ) : (
    <StartTrialButton />
  );

  const hasChanges = licenses.some((license) => license.expires_at);
  let hasPlan = lifetimeLicenseCount > 0;

  if (subscription) {
    const { status } = subscription;

    const isSubscriptionCancelled = status === "cancelled";
    const isSubscriptionNonRenewing = status === "non_renewing";
    const isInTrial = status === "in_trial";

    hasPlan = !isSubscriptionCancelled;

    if (isSubscriptionNonRenewing) {
      buttonComponent = (
        <RemoveChangeButton subscription={subscription} type="cancellation" />
      );
      tagComponent = <NonRenewingTag />;
    } else if (hasChanges) {
      buttonComponent = (
        <RemoveChangeButton subscription={subscription} type="change" />
      );
      tagComponent = <ScheduledChangeTag />;
    } else if (isSubscriptionCancelled) {
      buttonComponent = <ReactivateSubscriptionButton />;
    } else {
      buttonComponent = <ManageSubscriptionButton />;
      tagComponent = <Tag variant="success">Active</Tag>;
    }
    if (isInTrial) {
      tagComponent = <Tag variant="secondary">7-day free trial</Tag>;
    }
  }

  const isFree =
    !estimateRenewal?.amountDue ||
    !subscription ||
    subscription.status === "cancelled";

  let priceValue: string;
  let priceSubtext: string;
  if (isFree) {
    priceValue = "Free";
  } else {
    const { amountDue } = estimateRenewal;

    priceValue = formatPrice(amountDue / 100);
    priceSubtext = subscription.item_price.period_unit;
  }

  const priceComponent = (
    <Price
      priceValue={priceValue}
      priceSubtext={priceSubtext}
      isLoading={isLoadingEstimateRenewal}
    />
  );

  const mainComponent = hasPlan && (
    <SubscriptionCardSeatsSummary
      accounts={
        workspaceAccounts
          ? workspaceAccounts.filter(
              ({ license, type }) => type === "user" && !!license,
            )
          : []
      }
      availableSeatsCount={licenseCount}
      extraTagComponent={
        lifetimeLicenseCount && (
          <Tag variant="primary-black" size="sm">
            {lifetimeLicenseCount} lifetime
          </Tag>
        )
      }
    />
  );

  return (
    <SubscriptionDetailsCard
      plan="outreach"
      footerComponent={
        <SubscriptionInfo
          nextBillingAt={estimateRenewal?.nextBillingAt}
          isLoading={isLoadingEstimateRenewal}
          subscription={subscription}
          buttonComponent={
            <RestrictedComponent visibleForPermissions={["billing_full"]}>
              {buttonComponent}
            </RestrictedComponent>
          }
        />
      }
      tagComponent={tagComponent}
      priceComponent={priceComponent}
      mainComponent={mainComponent}
    />
  );
}
